import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { usePermission } from '@/permissions/usePermission';
import type {
  ProviderTransactionDto,
  StatementViewModel,
} from '@/api/types/providerStatements';
import { PROVIDER_STATEMENT_REGISTRY, PS_PERMS, type ProviderKey } from './config';
import { StatisticsTab } from './StatisticsTab';
import { UnmatchedTab } from './UnmatchedTab';
import { MatchedTab } from './MatchedTab';

const PAGE_SIZE = 5;

export function ProviderStatementPage({ provider }: { provider: ProviderKey }) {
  const config = PROVIDER_STATEMENT_REGISTRY[provider];
  const can = usePermission();
  const queryClient = useQueryClient();

  const perms = useMemo(
    () => ({
      uploadExcel: can(PS_PERMS.uploadExcel),
      match: can(PS_PERMS.match),
      unmatch: can(PS_PERMS.unmatch),
      delete: can(PS_PERMS.delete),
      manualSync: can(PS_PERMS.manualSync),
      editProvider: can(PS_PERMS.editProvider),
    }),
    [can],
  );

  const [reloadKey, setReloadKey] = useState(0);
  const [active, setActive] = useState('statistics');

  // Provider-not-found guard (Razor OnGetAsync redirects with a flash warning).
  const exists = useQuery({
    queryKey: ['provider-statement', config.key, 'exists'],
    queryFn: config.api.providerExists,
  });

  // The two Unmatched grids live at the page level so the Statistics tab can read
  // their recordsTotal, and both refetch together on reloadAll (via reloadKey).
  const transactions = useServerDataTable<ProviderTransactionDto>({
    queryKey: ['provider-statement', config.key, 'unmatched-transactions', reloadKey],
    fetcher: config.api.getUnmatchedTransactions,
    pageSize: PAGE_SIZE,
    enabled: exists.data === true,
  });

  const statements = useServerDataTable<StatementViewModel>({
    queryKey: ['provider-statement', config.key, 'unmatched-statements', reloadKey],
    fetcher: config.api.getUnmatchedStatements,
    pageSize: PAGE_SIZE,
    initialSort: { key: config.statementSortField, dir: 'desc' },
    enabled: exists.data === true,
  });

  const reloadAll = useCallback(() => {
    setReloadKey((k) => k + 1);
    void queryClient.invalidateQueries({ queryKey: ['provider-statement', config.key, 'summary'] });
  }, [queryClient, config.key]);

  const tabs: TabItem[] = [
    { key: 'statistics', title: 'Statistics' },
    { key: 'unmatched', title: 'Unmatched Transactions' },
    { key: 'matched', title: 'Matched Transactions' },
  ];

  if (exists.isLoading) {
    return (
      <div className="provider-statement-page">
        <PageHeader title={config.title} breadcrumb={['Home', config.title]} />
        <div className="text-center py-5 text-muted">
          <i className="fa fa-spinner fa-spin me-1" />
          Loading…
        </div>
      </div>
    );
  }

  if (exists.data === false) {
    return (
      <div className="provider-statement-page">
        <PageHeader title={config.title} breadcrumb={['Home', config.title]} />
        <div className="alert alert-warning">{config.title} Provider not found.</div>
      </div>
    );
  }

  return (
    <div className="provider-statement-page">
      <PageHeader
        title={config.title}
        breadcrumb={['Home', config.title]}
        actions={
          <AsyncButton
            className="btn btn-outline-primary btn-sm"
            icon="fa fa-download"
            onClick={() => config.api.downloadTemplate(`${config.title} Template.xlsx`)}
          >
            Download Template
          </AsyncButton>
        }
      />

      <div className="card custom-card mb-0">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" />
        </div>
        <div className="card-body">
          {active === 'statistics' && (
            <StatisticsTab
              config={config}
              can={{ uploadExcel: perms.uploadExcel, manualSync: perms.manualSync }}
              unmatchedStatementsCount={statements.totalCount}
              unmatchedTransactionsCount={transactions.totalCount}
              onReloadAll={reloadAll}
            />
          )}
          {active === 'unmatched' && (
            <UnmatchedTab
              config={config}
              transactions={transactions}
              statements={statements}
              can={{ match: perms.match, delete: perms.delete, editProvider: perms.editProvider }}
              onReloadAll={reloadAll}
            />
          )}
          {active === 'matched' && (
            <MatchedTab
              config={config}
              reloadKey={reloadKey}
              can={{ unmatch: perms.unmatch }}
              onReloadAll={reloadAll}
            />
          )}
        </div>
      </div>
    </div>
  );
}
